import {GAME_TITLES, MLBB_GAME_ID, PUBG_MOBILE_GAME_ID} from '@/constants/games'

export const GIVEAWAY_STATUS = {
    SCHEDULED: 'scheduled',
    ACTIVE: 'active',
    FINISHED: 'finished',
}

export const REWARD_STATUS = {
    WAITING_CONTACT: 'waiting_contact',
    CONTACTED: 'contacted',
    REWARDED: 'rewarded',
}

export const COUPON_STATUS = {
    AVAILABLE: 'available',
    ACTIVATED: 'activated',
    EXPIRED: 'expired',
}

export const WINNER_STATUS = {
    PENDING: 'pending',
    CONFIRMED: 'confirmed',
    EXPIRED: 'expired',
}

const pad = (value) => String(value).padStart(2, '0')

// Розыгрыши бывают только по этим двум играм (BIGPLAY-2659).
export const GIVEAWAY_GAME_IDS = [PUBG_MOBILE_GAME_ID, MLBB_GAME_ID]

/** id игры розыгрыша: бэк отдаёт и game_id, и game {id, slug, name}. */
export const getGiveawayGameId = (giveaway) => {
    const id = giveaway?.game_id ?? giveaway?.game?.id
    return id == null ? null : Number(id)
}

export const GiveawayAdapterMixin = {
    data() {
        return {
            giveawayStatusOptions: [
                {value: null, text: 'Все статусы'},
                {value: GIVEAWAY_STATUS.SCHEDULED, text: 'Ожидает старта'},
                {value: GIVEAWAY_STATUS.ACTIVE, text: 'Активен'},
                {value: GIVEAWAY_STATUS.FINISHED, text: 'Завершён'},
            ],
            giveawayGameOptions: GIVEAWAY_GAME_IDS.map(id => ({value: id, text: GAME_TITLES[id]})),
            rewardStatusOptions: [
                {value: REWARD_STATUS.WAITING_CONTACT, text: 'Ожидает связи'},
                {value: REWARD_STATUS.CONTACTED, text: 'Связались'},
                {value: REWARD_STATUS.REWARDED, text: 'Награда выдана'},
            ],
        }
    },
    methods: {
        /**
         * ISO 8601 с бэка -> YYYY-MM-DD HH:mm в часовом поясе браузера
         */
        formatGiveawayDate(value) {
            if (!value) return '—'
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return value

            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
                + ` ${pad(date.getHours())}:${pad(date.getMinutes())}`
        },
        /**
         * Date -> ISO 8601 со смещением часового пояса браузера (2026-09-09T15:05:00+05:00)
         */
        toIsoWithOffset(value) {
            if (!value) return null
            const date = value instanceof Date ? value : new Date(value)
            if (Number.isNaN(date.getTime())) return null

            const offsetMinutes = -date.getTimezoneOffset()
            const sign = offsetMinutes >= 0 ? '+' : '-'
            const absOffset = Math.abs(offsetMinutes)

            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
                + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
                + `${sign}${pad(Math.floor(absOffset / 60))}:${pad(absOffset % 60)}`
        },
        /**
         * Пустое описание купона отправляем как null
         */
        normalizeCouponDescription(value) {
            const description = (value || '').trim()
            return description || null
        },
        getGiveawayStatusText(giveaway) {
            switch (giveaway?.status) {
                case GIVEAWAY_STATUS.SCHEDULED: return 'Ожидает старта'
                case GIVEAWAY_STATUS.ACTIVE: return 'Активен'
                case GIVEAWAY_STATUS.FINISHED: return 'Завершён'
                default: return 'Неизвестно'
            }
        },
        getGiveawayStatusVariant(giveaway) {
            switch (giveaway?.status) {
                case GIVEAWAY_STATUS.SCHEDULED: return 'warning'
                case GIVEAWAY_STATUS.ACTIVE: return 'success'
                case GIVEAWAY_STATUS.FINISHED: return 'primary'
                default: return 'secondary'
            }
        },
        /** «PUBG MOBILE» / «MLBB»; у старых розыгрышей без игры — прочерк. */
        getGiveawayGameTitle(giveaway) {
            const id = getGiveawayGameId(giveaway)
            return GAME_TITLES[id] || '—'
        },
        isPubgGiveaway(giveaway) {
            return getGiveawayGameId(giveaway) === PUBG_MOBILE_GAME_ID
        },
        getCouponsTotal(giveaway) {
            return Number(giveaway?.coupons_count ?? 0)
        },
        getCouponsActivated(giveaway) {
            return Number(giveaway?.activated_coupons_count ?? 0)
        },
        getCouponsProgress(giveaway) {
            const total = this.getCouponsTotal(giveaway)
            if (!total) return 0
            return Math.min(100, Math.round((this.getCouponsActivated(giveaway) / total) * 100))
        },
        getRewardStatusText(rewardStatus) {
            const option = this.rewardStatusOptions.find(item => item.value === rewardStatus)
            return option ? option.text : 'Неизвестно'
        },
        getRewardStatusVariant(rewardStatus) {
            switch (rewardStatus) {
                case REWARD_STATUS.WAITING_CONTACT: return 'warning'
                case REWARD_STATUS.CONTACTED: return 'info'
                case REWARD_STATUS.REWARDED: return 'success'
                default: return 'secondary'
            }
        },
        getCouponStatusText(status) {
            switch (status) {
                case COUPON_STATUS.ACTIVATED: return 'Активирован'
                case COUPON_STATUS.EXPIRED: return 'Сгорел'
                default: return 'Доступен'
            }
        },
        getCouponStatusVariant(status) {
            switch (status) {
                case COUPON_STATUS.ACTIVATED: return 'success'
                case COUPON_STATUS.EXPIRED: return 'dark'
                default: return 'secondary'
            }
        },
        getWinnerStatusText(status) {
            switch (status) {
                case WINNER_STATUS.CONFIRMED: return 'Подтверждён'
                case WINNER_STATUS.EXPIRED: return 'Не успел'
                default: return 'Ожидает подтверждения'
            }
        },
        getWinnerStatusVariant(status) {
            switch (status) {
                case WINNER_STATUS.CONFIRMED: return 'success'
                case WINNER_STATUS.EXPIRED: return 'dark'
                default: return 'warning'
            }
        },
        /**
         * Награду выдаём только подтвердившим победителям
         */
        isWinnerConfirmed(winner) {
            return winner?.status === WINNER_STATUS.CONFIRMED
        },
        getContactProviderText(provider) {
            switch (provider) {
                case 'telegram': return 'Telegram'
                case 'discord': return 'Discord'
                default: return '—'
            }
        },
    },
}
